import type { HydraCollection } from '../types/entities';

/**
 * Lesen einer paginierten API-Platform-Collection SAMT Gesamtzahl und letzter Seite.
 *
 * Der zentrale Axios-Client schickt `Accept: application/json` (api.ts). In diesem Format
 * liefert API Platform 4 eine **nackte Liste** -- die Antwort traegt weder Gesamtzahl noch
 * Seitenangaben, obwohl serverseitig paginiert wird. Wer eine Seitenzahl oder ein "N Einträge"
 * anzeigen will, bekommt aus diesem Format schlicht keine Auskunft und muesste im Browser
 * zaehlen, was auf einer abgeschnittenen Seite falsch waere.
 *
 * `application/ld+json` liefert die Huelle mit `totalItems` und `member`. Die Praefixe `hydra:`
 * sind seit API Platform 4 abgeschaltet (`hydra_prefix` steht per Voreinstellung auf `false`),
 * gegen den laufenden Server geprueft -- deshalb werden hier BEIDE Schreibweisen gelesen und auf
 * die im Projekt eingefuehrte `HydraCollection`-Form normalisiert.
 *
 * Punkt 11 (IN-07, 35.1-06): Denselben Zwiespalt gibt es bei der letzten Seite. API Platforms
 * `PartialCollectionViewNormalizer` (`hydra_prefix: false`) liefert den Block `view` (ohne
 * Praefix) bzw. `hydra:view` mit `last`/`hydra:last` als IRI, sobald es mehr als eine Seite gibt
 * oder ein Filter gesetzt ist -- etwa `/api/emission_report_imports?year=2026&page=3`. Ohne
 * diesen Block (genau eine Seite, keine Filter) gilt Seite 1.
 */

/** Pro Request gesetzt, nicht global: nur die Aufrufe, die die Gesamtzahl brauchen, wechseln das Format. */
export const LD_JSON_ACCEPT = { headers: { Accept: 'application/ld+json' } } as const;

/** Was vom Server kommen kann: beide Schreibweisen der Huelle oder eine nackte Liste. */
export type CollectionResponse<T> =
  | ({
      member?: T[];
      totalItems?: number;
      view?: { last?: string };
      'hydra:view'?: { 'hydra:last'?: string };
    } & Partial<HydraCollection<T>>)
  | T[];

/** `HydraCollection` plus die letzte Seite laut Server (Punkt 11, IN-07). */
export interface PagedCollection<T> extends HydraCollection<T> {
  /** Aus `view.last`/`hydra:view.hydra:last`; ohne Block oder ohne `page`-Parameter gilt 1. */
  lastPage: number;
}

/** Liest den `page`-Parameter aus der `view.last`-IRI. Nur eine ganze Zahl ≥ 1 zaehlt, sonst 1. */
function lastPageFromViewIri(iri: string | undefined): number {
  if (iri === undefined) {
    return 1;
  }
  const pageParam = new URL(iri, 'http://localhost').searchParams.get('page');
  const page = pageParam === null ? NaN : Number(pageParam);
  return Number.isInteger(page) && page >= 1 ? page : 1;
}

/**
 * Normalisiert auf `{ 'hydra:member', 'hydra:totalItems', lastPage }`. Fehlt die Gesamtzahl
 * (nackte Liste), gilt die Laenge der gelieferten Menge -- die einzige Zahl, die es dann
 * ueberhaupt gibt. Fehlt der `view`-Block, gilt Seite 1.
 */
export function normalizeCollection<T>(data: CollectionResponse<T>): PagedCollection<T> {
  if (Array.isArray(data)) {
    return { 'hydra:member': data, 'hydra:totalItems': data.length, lastPage: 1 };
  }

  const member = data.member ?? data['hydra:member'] ?? [];
  const total = data.totalItems ?? data['hydra:totalItems'] ?? member.length;
  const lastPage = lastPageFromViewIri(data.view?.last ?? data['hydra:view']?.['hydra:last']);

  return { 'hydra:member': member, 'hydra:totalItems': total, lastPage };
}
